import { useState, useEffect } from "react";
import { ArrowLeft, CreditCard, Lock, Check, QrCode, Smartphone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import Header from "@/components/Header";
import { supabase } from "@/integrations/supabase/client";

// Extend Window interface for Razorpay
declare global {
  interface Window {
    Razorpay: any;
  }
}

const Checkout = () => {
  const { items, totalPrice, clearCart } = useCart();
  const { user, isLoading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [isProcessing, setIsProcessing] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const [razorpayLoaded, setRazorpayLoaded] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState<"razorpay" | "upi">("razorpay");
  const [upiTransactionId, setUpiTransactionId] = useState("");
  const [isUpiProcessing, setIsUpiProcessing] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    gender: "" as "male" | "female" | "",
    profession: "",
  });
  const totalTravelers = items.reduce((sum, item) => sum + item.quantity, 0);
  const [travelers, setTravelers] = useState<
    Array<{ fullName: string; gender: "male" | "female" | ""; age: string; phone: string }>
  >([{ fullName: "", gender: "", age: "", phone: "" }]);

  useEffect(() => {
    const nextCount = Math.max(totalTravelers, 1);
    setTravelers((prev) => {
      const next = [...prev];
      if (next.length < nextCount) {
        for (let i = next.length; i < nextCount; i += 1) {
          next.push({ fullName: "", gender: "", age: "", phone: "" });
        }
      }
      if (next.length > nextCount) {
        next.length = nextCount;
      }
      return next;
    });
  }, [totalTravelers]);

  useEffect(() => {
    setTravelers((prev) => {
      if (prev.length === 0) return prev;
      const next = [...prev];
      const currentFirst = next[0];
      next[0] = {
        ...currentFirst,
        fullName: `${formData.firstName} ${formData.lastName}`.trim(),
        gender: formData.gender,
        phone: formData.phone,
      };
      return next;
    });
  }, [formData.firstName, formData.lastName, formData.gender, formData.phone]);

  // Redirect to auth if not signed in
  useEffect(() => {
    if (!authLoading && !user) {
      toast.error("Please sign in to continue with checkout");
      navigate("/auth", { state: { returnTo: "/checkout" } });
    }
  }, [user, authLoading, navigate]);

  // Load Razorpay script
  useEffect(() => {
    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.async = true;
    script.onload = () => setRazorpayLoaded(true);
    script.onerror = () => {
      console.error("Failed to load Razorpay script");
      toast.error("Failed to load payment gateway");
    };
    document.body.appendChild(script);

    return () => {
      document.body.removeChild(script);
    };
  }, []);

  // Show loading or redirect message if not authenticated
  if (authLoading) {
    return (
      <>
        <Header />
        <div className="min-h-screen pt-24 pb-12 bg-background flex items-center justify-center">
          <div className="text-center">
            <p className="text-muted-foreground">Loading...</p>
          </div>
        </div>
      </>
    );
  }

  if (!user) {
    return null; // Will redirect via useEffect
  }

  const sendNotifications = async () => {
    try {
      const { data, error } = await supabase.functions.invoke("send-payment-confirmation", {
        body: {
          email: formData.email,
          phone: formData.phone,
          firstName: formData.firstName,
          lastName: formData.lastName,
          totalAmount: totalPrice,
          travelers: travelers.map((traveler, index) => ({
            travelerNo: index + 1,
            fullName: traveler.fullName,
            gender: traveler.gender,
            age: Number(traveler.age),
            phone: traveler.phone,
          })),
          items: items.map(item => ({
            title: item.title,
            destination: item.destination,
            duration: item.duration,
            quantity: item.quantity,
            price: item.price,
          })),
        },
      });

      if (error) {
        console.error("Failed to send notifications:", error);
        toast.error("Failed to send confirmation notifications.");
      } else {
        console.log("Notifications sent:", data);
        if (data.email === "sent") {
          toast.success("Confirmation email sent!");
        } else {
          toast.error("Customer email failed to send.", {
            description: data.emailError || "Please check email configuration.",
          });
        }
        if (data.sms === "sent") {
          toast.success("Confirmation SMS sent!");
        } else if (data.sms === "failed" && data.smsError) {
          console.warn("SMS send failed:", data.smsError);
        }
        if (data.adminEmail === "sent") {
          toast.success("Admin notification sent!");
        } else if (data.adminEmail === "failed") {
          toast.error("Admin notification failed.", {
            description: data.adminEmailError || "Please check admin email configuration.",
          });
        }
      }
    } catch (err) {
      console.error("Error sending notifications:", err);
    }
  };

  const saveGender = async () => {
    if (!user || !formData.gender) return;
    
    try {
      const { error: profileError } = await supabase
        .from("profiles")
        .upsert({
          id: user.id,
          gender: formData.gender,
          updated_at: new Date().toISOString(),
        }, {
          onConflict: 'id'
        });

      if (profileError) {
        console.error("Error saving gender:", profileError);
      }
    } catch (err) {
      console.error("Error saving gender:", err);
    }
  };

  const validateTravelerDetails = () => {
    const requiredCount = Math.max(totalTravelers, 1);
    if (travelers.length < requiredCount) {
      toast.error("Please fill traveler details for all travelers.");
      return false;
    }

    for (let i = 0; i < requiredCount; i += 1) {
      const traveler = travelers[i];
      if (!traveler.fullName.trim() || !traveler.gender || !traveler.age || !traveler.phone.trim()) {
        toast.error(`Please fill all required fields for Traveler ${i + 1}.`);
        return false;
      }

      const age = Number(traveler.age);
      if (Number.isNaN(age) || age < 18) {
        toast.error(`Traveler ${i + 1} must be 18+.`);
        return false;
      }
    }

    return true;
  };

  const saveBooking = async (paymentResponse: any, orderId: string) => {
    try {
      const bookingData = {
        user_id: user?.id || null,
        razorpay_order_id: orderId,
        razorpay_payment_id: paymentResponse.razorpay_payment_id || "",
        customer_name: `${formData.firstName} ${formData.lastName}`,
        customer_email: formData.email,
        customer_phone: formData.phone,
        profession: formData.profession,
        total_amount: totalPrice,
        currency: "INR",
        status: "completed",
        items: items.map((item) => ({
          title: item.title,
          destination: item.destination,
          duration: item.duration,
          quantity: item.quantity,
          price: item.price,
          image: item.image,
        })),
      };

      console.log("Saving booking with data:", {
        user_id: bookingData.user_id,
        customer_email: bookingData.customer_email,
        total_amount: bookingData.total_amount,
      });

      const { data, error } = await supabase.from("bookings").insert(bookingData).select();

      if (error) {
        console.error("Failed to save booking:", error);
        toast.error("Failed to save booking. Please contact support.");
      } else {
        console.log("Booking saved successfully:", data);
        // Save gender after successful booking
        await saveGender();
      }
    } catch (err) {
      console.error("Error saving booking:", err);
      toast.error("An error occurred while saving your booking.");
    }
  };

  const handlePaymentSuccess = async (response: any, orderId: string) => {
    console.log("Payment successful:", response);
    
    // Save booking to database
    await saveBooking(response, orderId);
    
    // Send notifications in the background
    sendNotifications();
    
    setIsComplete(true);
    clearCart();
    
    toast.success("Payment successful!", {
      description: "Your adventure booking is confirmed!",
    });
  };

  const handlePaymentError = (error: any) => {
    console.error("Payment failed:", error);
    setIsProcessing(false);
    toast.error("Payment failed", {
      description: error.description || "Please try again",
    });
  };

  const initiatePayment = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!razorpayLoaded) {
      toast.error("Payment gateway is loading. Please wait...");
      return;
    }

    // Validate form
    if (!formData.firstName || !formData.lastName || !formData.email || !formData.phone || !formData.gender || !formData.profession) {
      toast.error("Please fill all required fields including gender and profession");
      return;
    }
    if (!validateTravelerDetails()) {
      return;
    }

    setIsProcessing(true);

    try {
      // Create order on backend
      const { data, error } = await supabase.functions.invoke("create-razorpay-order", {
        body: {
          amount: totalPrice,
          currency: "INR",
          receipt: `trip_${Date.now()}`,
          notes: {
            customerName: `${formData.firstName} ${formData.lastName}`,
            customerEmail: formData.email,
            customerPhone: formData.phone,
          },
        },
      });

      if (error || !data?.orderId) {
        throw new Error(error?.message || data?.error || "Failed to create order");
      }

      console.log("Order created:", data);

      // Initialize Razorpay checkout
      const options = {
        key: data.keyId,
        amount: data.amount,
        currency: data.currency,
        name: "Slateus",
        description: `Booking for ${items.length} trip(s)`,
        order_id: data.orderId,
        prefill: {
          name: `${formData.firstName} ${formData.lastName}`,
          email: formData.email,
          contact: formData.phone,
        },
        theme: {
          color: "#FF6B35",
        },
        handler: (response: any) => handlePaymentSuccess(response, data.orderId),
        modal: {
          ondismiss: () => {
            setIsProcessing(false);
            toast.info("Payment cancelled");
          },
        },
      };

      const razorpay = new window.Razorpay(options);
      razorpay.on("payment.failed", handlePaymentError);
      razorpay.open();
      
    } catch (err: any) {
      console.error("Error initiating payment:", err);
      setIsProcessing(false);
      toast.error("Failed to initiate payment", {
        description: err.message || "Please try again",
      });
    }
  };

  const handleUpiPayment = async () => {
    // Validate form
    if (!formData.firstName || !formData.lastName || !formData.email || !formData.phone || !formData.gender || !formData.profession) {
      toast.error("Please fill all required fields including gender and profession");
      return;
    }
    if (!validateTravelerDetails()) {
      return;
    }

    if (!upiTransactionId.trim()) {
      toast.error("Please enter UPI Transaction ID");
      return;
    }

    // Validate transaction ID format (12 digit number typically)
    const trimmedId = upiTransactionId.trim();
    if (trimmedId.length < 8) {
      toast.error("Please enter a valid UPI Transaction ID");
      return;
    }

    setIsUpiProcessing(true);

    try {
      // Save booking with UPI transaction details
      const { error } = await supabase.from("bookings").insert({
        user_id: user?.id || null,
        razorpay_order_id: `UPI_${trimmedId}`,
        razorpay_payment_id: trimmedId,
        customer_name: `${formData.firstName} ${formData.lastName}`,
        customer_email: formData.email,
        customer_phone: formData.phone,
        profession: formData.profession,
        total_amount: totalPrice,
        currency: "INR",
        status: "pending_verification",
        items: items.map((item) => ({
          title: item.title,
          destination: item.destination,
          duration: item.duration,
          quantity: item.quantity,
          price: item.price,
          image: item.image,
        })),
      });

      if (error) {
        throw error;
      }

      console.log("UPI Booking saved successfully");
      
      // Save gender after successful booking
      await saveGender();
      
      // Send notifications
      sendNotifications();
      
      setIsComplete(true);
      clearCart();
      
      toast.success("Booking submitted!", {
        description: "Your payment will be verified shortly.",
      });
    } catch (err: any) {
      console.error("Error saving UPI booking:", err);
      toast.error("Failed to submit booking", {
        description: err.message || "Please try again",
      });
    } finally {
      setIsUpiProcessing(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  if (items.length === 0 && !isComplete) {
    return (
      <>
        <Header />
        <div className="min-h-screen pt-24 pb-12 bg-background">
          <div className="container mx-auto px-4 text-center">
            <h1 className="font-display text-3xl font-bold text-foreground mb-4">Your cart is empty</h1>
            <p className="text-muted-foreground mb-8">Add some trips before checking out.</p>
            <Button asChild>
              <Link to="/">Browse Trips</Link>
            </Button>
          </div>
        </div>
      </>
    );
  }

  if (isComplete) {
    return (
      <>
        <Header />
        <div className="min-h-screen pt-24 pb-12 bg-background flex items-center justify-center">
          <div className="text-center animate-fade-in">
            <div className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-6">
              <Check className="w-10 h-10 text-primary" />
            </div>
            <h1 className="font-display text-3xl md:text-4xl font-bold text-foreground mb-4">
              Booking Confirmed!
            </h1>
            <p className="text-lg text-muted-foreground mb-8 max-w-md mx-auto">
              Thank you for booking with Slate us. You'll receive a confirmation email shortly with all the details.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              {user && (
                <Button asChild size="lg" variant="default">
                  <Link to="/dashboard">View My Bookings</Link>
                </Button>
              )}
              <Button asChild size="lg" variant={user ? "outline" : "default"}>
                <Link to="/">Back to Home</Link>
              </Button>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Header />
      <div className="min-h-screen pt-24 pb-12 bg-gradient-sunset">
        <div className="container mx-auto px-4">
          <Button variant="ghost" className="mb-6 gap-2" asChild>
            <Link to="/">
              <ArrowLeft className="w-4 h-4" />
              Back to Trips
            </Link>
          </Button>

          <div className="grid lg:grid-cols-2 gap-8">
            {/* Payment Form */}
            <div className="animate-fade-in">
              <Card className="border-0 shadow-card">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 font-display text-2xl">
                    <CreditCard className="w-6 h-6 text-primary" />
                    Contact Details
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <form onSubmit={initiatePayment} className="space-y-6">
                    <div className="space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="firstName">First Name</Label>
                          <Input 
                            id="firstName" 
                            name="firstName" 
                            placeholder="John" 
                            required 
                            value={formData.firstName}
                            onChange={handleInputChange}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="lastName">Last Name</Label>
                          <Input 
                            id="lastName" 
                            name="lastName" 
                            placeholder="Doe" 
                            required 
                            value={formData.lastName}
                            onChange={handleInputChange}
                          />
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="email">Email</Label>
                        <Input 
                          id="email" 
                          name="email" 
                          type="email" 
                          placeholder="john@example.com" 
                          required 
                          value={formData.email}
                          onChange={handleInputChange}
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="phone">Phone Number</Label>
                        <Input 
                          id="phone" 
                          name="phone" 
                          type="tel" 
                          placeholder="+91 98765 43210" 
                          required 
                          value={formData.phone}
                          onChange={handleInputChange}
                        />
                      </div>

                      <div className="space-y-2">
                        <Label>Gender <span className="text-destructive">*</span></Label>
                        <RadioGroup 
                          value={formData.gender} 
                          onValueChange={(value) => setFormData(prev => ({ ...prev, gender: value as "male" | "female" }))} 
                          className="flex gap-6"
                        >
                          <div className="flex items-center space-x-2">
                            <RadioGroupItem value="male" id="gender-male" />
                            <Label htmlFor="gender-male" className="cursor-pointer">Male</Label>
                          </div>
                          <div className="flex items-center space-x-2">
                            <RadioGroupItem value="female" id="gender-female" />
                            <Label htmlFor="gender-female" className="cursor-pointer">Female</Label>
                          </div>
                        </RadioGroup>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="profession">Profession <span className="text-destructive">*</span></Label>
                        <Input 
                          id="profession" 
                          name="profession" 
                          placeholder="Software Engineer" 
                          required 
                          value={formData.profession}
                          onChange={handleInputChange}
                        />
                      </div>

                      <div className="space-y-4 pt-4 border-t border-border">
                        <div>
                          <Label className="text-base font-semibold">
                            Traveler Details ({Math.max(totalTravelers, 1)})
                          </Label>
                          <p className="text-xs text-muted-foreground mt-1">
                            Please enter details for all travelers. Age must be 18+ for every traveler.
                          </p>
                        </div>
                        {travelers.map((traveler, index) => (
                          <div key={`traveler-${index}`} className="p-4 rounded-lg border border-border space-y-3">
                            <p className="text-sm font-medium text-foreground">
                              Traveler {index + 1}
                            </p>
                            <div className="space-y-2">
                              <Label htmlFor={`traveler-name-${index}`}>Full Name</Label>
                              <Input
                                id={`traveler-name-${index}`}
                                placeholder="Enter full name"
                                value={traveler.fullName}
                                onChange={(e) =>
                                  setTravelers((prev) =>
                                    prev.map((t, i) =>
                                      i === index ? { ...t, fullName: e.target.value } : t,
                                    ),
                                  )
                                }
                              />
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                              <div className="space-y-2">
                                <Label htmlFor={`traveler-age-${index}`}>Age (18+)</Label>
                                <Input
                                  id={`traveler-age-${index}`}
                                  type="number"
                                  min={18}
                                  placeholder="18"
                                  value={traveler.age}
                                  onChange={(e) =>
                                    setTravelers((prev) =>
                                      prev.map((t, i) =>
                                        i === index ? { ...t, age: e.target.value } : t,
                                      ),
                                    )
                                  }
                                />
                              </div>
                              <div className="space-y-2">
                                <Label htmlFor={`traveler-phone-${index}`}>Phone Number</Label>
                                <Input
                                  id={`traveler-phone-${index}`}
                                  type="tel"
                                  placeholder="+91 98765 43210"
                                  value={traveler.phone}
                                  onChange={(e) =>
                                    setTravelers((prev) =>
                                      prev.map((t, i) =>
                                        i === index ? { ...t, phone: e.target.value } : t,
                                      ),
                                    )
                                  }
                                />
                              </div>
                            </div>
                            <div className="space-y-2">
                              <Label>Gender</Label>
                              <RadioGroup
                                value={traveler.gender}
                                onValueChange={(value) =>
                                  setTravelers((prev) =>
                                    prev.map((t, i) =>
                                      i === index
                                        ? { ...t, gender: value as "male" | "female" }
                                        : t,
                                    ),
                                  )
                                }
                                className="flex gap-6"
                              >
                                <div className="flex items-center space-x-2">
                                  <RadioGroupItem value="male" id={`traveler-gender-male-${index}`} />
                                  <Label htmlFor={`traveler-gender-male-${index}`}>Male</Label>
                                </div>
                                <div className="flex items-center space-x-2">
                                  <RadioGroupItem value="female" id={`traveler-gender-female-${index}`} />
                                  <Label htmlFor={`traveler-gender-female-${index}`}>Female</Label>
                                </div>
                              </RadioGroup>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Payment Method Selection */}
                    <div className="pt-4 border-t border-border">
                      <p className="text-sm font-medium text-foreground mb-3">Choose Payment Method</p>
                      <div className="grid grid-cols-2 gap-3 mb-4">
                        <button
                          type="button"
                          onClick={() => setPaymentMethod("razorpay")}
                          className={`p-4 rounded-lg border-2 transition-all ${
                            paymentMethod === "razorpay"
                              ? "border-primary bg-primary/5"
                              : "border-border hover:border-muted-foreground"
                          }`}
                        >
                          <CreditCard className="w-6 h-6 mx-auto mb-2 text-primary" />
                          <p className="text-sm font-medium text-foreground">Razorpay</p>
                          <p className="text-xs text-muted-foreground">Cards, UPI, Wallets</p>
                        </button>
                        <button
                          type="button"
                          onClick={() => setPaymentMethod("upi")}
                          className={`p-4 rounded-lg border-2 transition-all ${
                            paymentMethod === "upi"
                              ? "border-primary bg-primary/5"
                              : "border-border hover:border-muted-foreground"
                          }`}
                        >
                          <QrCode className="w-6 h-6 mx-auto mb-2 text-primary" />
                          <p className="text-sm font-medium text-foreground">UPI QR</p>
                          <p className="text-xs text-muted-foreground">Scan & Pay</p>
                        </button>
                      </div>

                      {paymentMethod === "razorpay" && (
                        <div className="flex items-center gap-4 p-4 rounded-lg bg-muted/50 mb-4">
                          <div className="flex-1">
                            <p className="text-sm font-medium text-foreground">Secure Payment via Razorpay</p>
                            <p className="text-xs text-muted-foreground">
                              Cards, UPI, Net Banking, Wallets & more
                            </p>
                          </div>
                          <div className="flex gap-1">
                            <div className="w-8 h-5 bg-background rounded border border-border flex items-center justify-center text-[8px] font-bold text-blue-600">VISA</div>
                            <div className="w-8 h-5 bg-background rounded border border-border flex items-center justify-center text-[8px] font-bold text-red-500">MC</div>
                            <div className="w-8 h-5 bg-background rounded border border-border flex items-center justify-center text-[8px] font-bold text-green-600">UPI</div>
                          </div>
                        </div>
                      )}

                      {paymentMethod === "upi" && (
                        <div className="p-4 rounded-lg bg-muted/50 mb-4">
                          <div className="flex justify-center mb-4">
                            <img 
                              src="/upi-qr-code.jpeg" 
                              alt="UPI QR Code - Scan to Pay" 
                              className="w-48 h-48 object-contain rounded-lg border border-border bg-white p-2"
                            />
                          </div>
                          <p className="text-xs text-muted-foreground text-center mb-1">
                            Scan & pay with any UPI app
                          </p>
                          <div className="flex justify-center gap-2 mb-2">
                            <Smartphone className="w-4 h-4 text-muted-foreground" />
                            <span className="text-xs text-muted-foreground">GPay • PhonePe • Paytm</span>
                          </div>
                          <p className="text-sm text-center font-medium text-foreground mb-4">
                            Pay to: RISHI RAJ SINGH
                          </p>
                          <p className="text-lg text-center font-bold text-primary mb-4">
                            Amount: ₹{totalPrice.toLocaleString('en-IN')}
                          </p>
                          
                          <div className="space-y-2">
                            <Label htmlFor="upiTransactionId">UPI Transaction ID / UTR Number</Label>
                            <Input 
                              id="upiTransactionId"
                              placeholder="Enter 12-digit transaction ID"
                              value={upiTransactionId}
                              onChange={(e) => setUpiTransactionId(e.target.value)}
                              className="text-center font-mono"
                            />
                            <p className="text-xs text-muted-foreground text-center">
                              Enter the transaction ID from your UPI app after payment
                            </p>
                          </div>
                        </div>
                      )}
                    </div>

                    {paymentMethod === "razorpay" ? (
                      <Button 
                        type="submit" 
                        className="w-full gap-2" 
                        size="lg"
                        disabled={isProcessing || !razorpayLoaded}
                      >
                        {isProcessing ? (
                          <>Processing...</>
                        ) : !razorpayLoaded ? (
                          <>Loading...</>
                        ) : (
                          <>
                            <Lock className="w-4 h-4" />
                            Pay ₹{totalPrice.toLocaleString('en-IN')}
                          </>
                        )}
                      </Button>
                    ) : (
                      <Button 
                        type="button"
                        onClick={handleUpiPayment}
                        className="w-full gap-2" 
                        size="lg"
                        disabled={isUpiProcessing || !upiTransactionId.trim()}
                      >
                        {isUpiProcessing ? (
                          <>Verifying...</>
                        ) : (
                          <>
                            <Check className="w-4 h-4" />
                            Confirm UPI Payment
                          </>
                        )}
                      </Button>
                    )}

                    <p className="text-xs text-center text-muted-foreground flex items-center justify-center gap-1">
                      <Lock className="w-3 h-3" />
                      Your payment is secure and encrypted
                    </p>
                  </form>
                </CardContent>
              </Card>
            </div>

            {/* Order Summary */}
            <div className="animate-fade-in" style={{ animationDelay: "0.1s" }}>
              <Card className="border-0 shadow-card sticky top-24">
                <CardHeader>
                  <CardTitle className="font-display text-2xl">Order Summary</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {items.map((item) => (
                    <div key={item.id} className="flex gap-4 pb-4 border-b border-border last:border-0">
                      <img
                        src={item.image}
                        alt={item.title}
                        className="w-20 h-20 object-cover rounded-lg"
                      />
                      <div className="flex-1">
                        <h4 className="font-medium text-foreground">{item.title}</h4>
                        <p className="text-sm text-muted-foreground">{item.destination}</p>
                        <p className="text-sm text-muted-foreground">{item.duration}</p>
                        <div className="flex items-center justify-between mt-1">
                          <span className="text-sm text-muted-foreground">Qty: {item.quantity}</span>
                          <span className="font-semibold text-foreground">₹{(item.price * item.quantity).toLocaleString('en-IN')}</span>
                        </div>
                      </div>
                    </div>
                  ))}

                  <div className="pt-4 space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Subtotal</span>
                      <span className="text-foreground">₹{totalPrice.toLocaleString('en-IN')}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Processing Fee</span>
                      <span className="text-foreground">₹0</span>
                    </div>
                    <div className="flex justify-between pt-4 border-t border-border">
                      <span className="text-lg font-semibold text-foreground">Total</span>
                      <span className="text-2xl font-bold text-primary">₹{totalPrice.toLocaleString('en-IN')}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Checkout;
